import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">
          Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Welcome to Autograph ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you use our video template processing platform.
            </p>
            <p className="text-gray-700">
              By using Autograph, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our service.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, and profile information</li>
              <li><strong>Content:</strong> Videos, images, audio files, text, and other media you upload or create</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through third-party payment processors</li>
              <li><strong>Communications:</strong> Messages, feedback, and support requests you send to us</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent, and interaction patterns</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
              <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, and analytics cookies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Analytics and Performance</h3>
            <p className="text-gray-700 mb-4">
              We use analytics tools to understand how users interact with our platform and improve our services.
              This may include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>User interaction patterns and feature usage</li>
              <li>Page navigation and session duration</li>
              <li>Technical errors and performance metrics</li>
              <li>Aggregate behavior analysis</li>
            </ul>
            <p className="text-gray-700">
              These tools automatically mask sensitive information such as passwords and payment details.
              Analytics data is used solely to improve user experience and identify technical issues.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our video editing platform</li>
              <li><strong>Account Management:</strong> To create and manage your account, authenticate users, and provide customer support</li>
              <li><strong>Content Processing:</strong> To process, render, and store your video projects and templates</li>
              <li><strong>Analytics:</strong> To understand usage patterns, improve features, and optimize performance</li>
              <li><strong>Communication:</strong> To send service updates, technical notices, and respond to inquiries</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
            </ul>
          </section>

          {/* Data Storage and Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Encryption:</strong> Data is encrypted in transit using SSL/TLS and at rest using AES-256</li>
              <li><strong>Access Controls:</strong> Strict access controls and authentication mechanisms</li>
              <li><strong>Cloud Storage:</strong> Media files are stored securely on Cloudflare R2 with redundancy</li>
              <li><strong>Database Security:</strong> MongoDB with authentication and network isolation</li>
              <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
            </ul>
            <p className="text-gray-700">
              While we strive to protect your information, no method of transmission over the internet is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">
              <strong>We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</strong>
            </p>
            <p className="text-gray-700 mb-4">
              We may share your information in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Service Providers:</strong> Trusted third-party vendors who perform services on our behalf (cloud hosting, analytics, payment processing). These providers are contractually obligated to protect your information.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, legal process, or government request</li>
              <li><strong>Safety and Security:</strong> To protect the rights, property, or safety of Autograph, our users, or the public</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, reorganization, or sale of assets (you will be notified of any such change)</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information for a specific purpose</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              Our platform may integrate with third-party services to provide functionality, analytics, and content.
              These services operate independently and have their own privacy policies:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Authentication Services:</strong> For secure sign-in and account management</li>
              <li><strong>Cloud Storage:</strong> For secure media file storage and delivery</li>
              <li><strong>Analytics Providers:</strong> For understanding usage patterns and improving our service</li>
              <li><strong>Stock Media Libraries:</strong> For accessing royalty-free images, videos, and audio</li>
              <li><strong>Payment Processors:</strong> For secure payment processing (we do not store payment card details)</li>
            </ul>
            <p className="text-gray-700">
              We carefully select service providers that maintain high standards of data protection. However,
              we are not responsible for the privacy practices of third-party services. We encourage you to
              review their privacy policies.
            </p>
          </section>

          {/* Your Rights and Choices */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Export:</strong> Download your projects and content in a portable format</li>
              <li><strong>Opt-Out:</strong> Disable analytics tracking (note: this may affect functionality)</li>
              <li><strong>Object:</strong> Object to processing of your information for certain purposes</li>
            </ul>
            <p className="text-gray-700">
              To exercise these rights, please contact us at <a href="mailto:info@autograph.video" className="text-blue-600 hover:underline">info@autograph.video</a>
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">We use cookies and similar tracking technologies to provide and improve our service:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for authentication, security, and core functionality</li>
              <li><strong>Functional Cookies:</strong> Remember your settings, preferences, and recent projects</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
              <li><strong>Performance Cookies:</strong> Monitor and improve application performance</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Most web browsers automatically accept cookies, but you can modify your browser settings to decline
              cookies if you prefer. Please note that disabling certain cookies may limit your ability to use
              some features of our service.
            </p>
            <p className="text-gray-700">
              For more information about cookies and how to manage them, visit{' '}
              <a href="https://www.allaboutcookies.org" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                www.allaboutcookies.org
              </a>
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Data Retention</h2>
            <p className="text-gray-700 mb-4">We retain your information for as long as necessary to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Provide our services and maintain your account</li>
              <li>Comply with legal obligations and resolve disputes</li>
              <li>Enforce our agreements and protect our rights</li>
            </ul>
            <p className="text-gray-700">
              When you delete your account, we will delete or anonymize your personal information within 30 days, 
              except where retention is required by law.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700">
              Our service is not intended for children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If you believe we have collected information from a child under 13, 
              please contact us immediately.
            </p>
          </section>

          {/* International Data Transfers */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
            <p className="text-gray-700">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. We ensure appropriate safeguards are in place 
              to protect your information in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use 
              of the service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Contact Information:</strong></p>
              <p className="text-gray-700">Email: <a href="mailto:info@autograph.video" className="text-blue-600 hover:underline">info@autograph.video</a></p>
            </div>
          </section>

          {/* GDPR and CCPA */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Additional Rights for EU and California Residents</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">GDPR (EU Residents)</h3>
            <p className="text-gray-700 mb-4">
              If you are located in the European Economic Area (EEA), you have additional rights under GDPR:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Right to data portability</li>
              <li>Right to restrict processing</li>
              <li>Right to object to automated decision-making</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">CCPA (California Residents)</h3>
            <p className="text-gray-700 mb-4">
              If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising your rights</li>
            </ul>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-center text-gray-600">
            © {new Date().getFullYear()} Autograph. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
